import React, { useMemo } from 'react'
import { getTime } from 'date-fns'
import { omitBy, isUndefined } from '@golden/utils'
import { ActivityType, ActivityUefaBuildingUpStateType } from '@golden/gdk-campaign'
import Grid from '@material-ui/core/Grid'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import FormField from '../../default/form/FormField'
import DropDown, { PropTypes as DropDownProps } from '../../default/form/DropDown'
import { FormState, ValueGetter } from '../../../utils/default/FormHook'
import { ActivityFormType, ActivityRequest } from './ActivityRecordDetailForm'
import useT from '../../../i18ns/admin/useT'
import { getValueFromChangeEvent, convertEmptyToUndefined } from '../../../utils/default/FormHelper'
import { SearchToRequestFunc } from '../../../utils/default/ComplexFlowHook'
import { uefaBuildingUpStatusName } from '../../../constants/default/activityStatusName'

export interface ActivityUefaBuildingUpFormType {
  account: string
  agent_account: string
  uefaBuildingUpStatus: ActivityUefaBuildingUpStateType | '--'
}

export const initialForm = (): ActivityUefaBuildingUpFormType => ({
  account: '',
  agent_account: '',
  uefaBuildingUpStatus: '--'
})

export const getValueFromEvent: ValueGetter<ActivityUefaBuildingUpFormType> = {
  account: getValueFromChangeEvent,
  agent_account: getValueFromChangeEvent,
  uefaBuildingUpStatus: getValueFromChangeEvent
}

export const formToRequest = (form: ActivityFormType): ActivityRequest => {
  const converted = {
    activity: ActivityType.UEFA_BUILDING_UP,
    account: convertEmptyToUndefined(form.account),
    agent_account: convertEmptyToUndefined(form.agent_account),
    state: form.uefaBuildingUpStatus === '--' ? undefined : form.uefaBuildingUpStatus,
    type: form.type,
    start_at: form.time.start === null ? undefined : getTime(form.time.start),
    end_at: form.time.end === null ? undefined : getTime(form.time.end),
    page: 1
  } as ActivityRequest
  return omitBy(converted, isUndefined) as ActivityRequest
}

export const searchToRequest: SearchToRequestFunc<{
  account: string | undefined
  agent_account: string | undefined
  state: ActivityUefaBuildingUpStateType | '--'
}> = (search) => {
  const converted = {
    account: search.account ? search.account : (search.account ? search.account : undefined),
    agent_account: search.agent_account,
    uefaBuildingUpStatus: search.state ? search.state : '--'
  }
  return omitBy(converted, isUndefined) as any
}

interface PropTypes {
  context: React.Context<FormState<ActivityFormType>>
}

const TextField = React.memo(MuiTextField)

const ActivityRecordUefaBuildingUpForm: React.FC<PropTypes> = (props) => {
  const { context } = props
  const { t } = useT()

  const stateOptions = useMemo(() => {
    return [{ name: t('common.all'), value: '--' }]
      .concat(
        Object.keys(uefaBuildingUpStatusName)
          .map((key) => ({ name: t(uefaBuildingUpStatusName[key as ActivityUefaBuildingUpStateType]), value: key })) as any
      )
  }, [t])

  return (
    <React.Fragment>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="account"
          label={t('common.playerAccount')}
          placeholder={t('placeholder.inputPlayerAccount')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="agent_account"
          label={t('common.agentAccount')}
          placeholder={t('placeholder.inputAgentAccount')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, DropDownProps>
          context={context}
          component={DropDown}
          name="uefaBuildingUpStatus"
          label={t('common.status')}
          options={stateOptions}
          fullWidth
        />
      </Grid>
    </React.Fragment>
  )
}

export default React.memo(ActivityRecordUefaBuildingUpForm)
